import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export function BidModal({
  open,
  onClose,
  onConfirm,
  basePrice = 2.9, // Eco base price
  defaultPickupDate = "",
  defaultPickupTime = "",
  defaultTargetDate = "",
  defaultTargetTime = "",
}) {
  const { t } = useTranslation();

  const [price, setPrice] = useState(basePrice);
  const [pickupDate, setPickupDate] = useState(defaultPickupDate);
  const [pickupTime, setPickupTime] = useState(defaultPickupTime);
  const [targetDate, setTargetDate] = useState(defaultTargetDate);
  const [targetTime, setTargetTime] = useState(defaultTargetTime);

  // Reset price when modal opens with new basePrice
  useEffect(() => {
    if (open) {
      setPrice(basePrice);
    }
  }, [open, basePrice]);

  const handleClose = () => {
    // Reset to defaults
    setPrice(basePrice);
    setPickupDate(defaultPickupDate);
    setPickupTime(defaultPickupTime);
    setTargetDate(defaultTargetDate);
    setTargetTime(defaultTargetTime);
    onClose();
  };

  const handleConfirm = () => {
    if (!price || price <= 0) {
      return;
    }

    onConfirm?.({
      price: Number(price),
      pickupDate,
      pickupTime,
      targetDate,
      targetTime,
    });

    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          {t("delivery.bidTitle") || "Set Your Bid"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t("delivery.bidWarning") || 
              "Note: Bidding is not guaranteed to find a courier. Your delivery may not be accepted if the price is too low."}
          </Alert>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("delivery.yourPrice") || "Your Price"}
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              label={t("delivery.priceLabel") || "Price (€)"}
              inputProps={{
                min: basePrice,
                step: 0.1,
              }}
              required
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("delivery.pickupTime") || "Pickup Time (Optional)"}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                label={t("delivery.date") || "Date"}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                label={t("delivery.time") || "Time"}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("delivery.targetTime") || "Target Delivery Time (Optional)"}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                label={t("delivery.date") || "Date"}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="time"
                value={targetTime}
                onChange={(e) => setTargetTime(e.target.value)}
                label={t("delivery.time") || "Time"}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          {t("common.cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!price || price <= 0}
        >
          {t("common.confirm") || "Confirm Bid"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
